import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { Box } from '@gemini/core';
import { Button } from '@gemini/ui';
import { MFE_IDENTIFIER } from '@price-pages-agency-cards/src/constants';
import { GERMAN_PROFILE_ANCHOR, mediums } from '@price-pages-agency-cards/src/constants/tracking';
import DE_TRANSLATIONS from '@price-pages-agency-cards/src/locales/de/translation.json';
import EN_TRANSLATIONS from '@price-pages-agency-cards/src/locales/en/translation.json';
import FR_TRANSLATIONS from '@price-pages-agency-cards/src/locales/fr/translation.json';
import NL_TRANSLATIONS from '@price-pages-agency-cards/src/locales/nl/translation.json';
import { sendAnalytics } from '@price-pages-agency-cards/src/presentation/helpers/tracking';
import { useAgencyStore } from '@price-pages-agency-cards/src/presentation/hooks/useAgencyHook';
import { EventPlace } from '@shared-utils/domain/event.entity';
import { isPlural } from '@shared-utils/helpers/lokalise';
import { dispatchStatsEvent } from '@shared-utils/helpers/statsEvent';
import { InternalEventType } from '@shared-utils/helpers/statsEvent/statsEvent.type';
import { generateAVItemPricePages } from '@shared-utils/helpers/tracking';
import { useBreakpoint } from '@shared-utils/helpers/viewport';
import { useLoadTranslations } from '@shared-utils/hooks/useLoadTranslations';
import { AV_LINK_TEXT, AV_LIST_NAME, AV_ZONE, TRACKING_EVENT_TYPE, } from '@shared-utils/types/analytics.types';
import Address from '@shared-utils/ui/components/Address';
import Logo from '@shared-utils/ui/components/Logo';
import PropertiesForSale from '@shared-utils/ui/components/PropertiesForSale';
import Rating from '@shared-utils/ui/components/Rating';
import { StandaloneErrorCard } from '../StandaloneErrorCard';
import { StandaloneSkeleton } from '../StandaloneSkeleton';
const AGENCY_NAME_SIZE = '60%';
const DESKTOP_WIDTH = '280px';
const MOBILE_WIDTH = '100%';
const TRACKING_PARENT_ID = 'agencyCardMap-container';
const Standalone = ({ agencyId }) => {
    useLoadTranslations(MFE_IDENTIFIER, {
        de: DE_TRANSLATIONS,
        en: EN_TRANSLATIONS,
        fr: FR_TRANSLATIONS,
        nl: NL_TRANSLATIONS,
    });
    const { t } = useTranslation(MFE_IDENTIFIER);
    const { isMobile } = useBreakpoint();
    const getAgencyById = useAgencyStore((state) => state.getAgencyById);
    const isFetchAgencyByIdLoading = useAgencyStore((state) => state.isFetchAgencyByIdLoading);
    const lastZipCode = useAgencyStore((state) => state.lastZipCode);
    const agency = getAgencyById(agencyId);
    useEffect(() => {
        if (agency) {
            const avItem = [
                generateAVItemPricePages(agency, AV_LIST_NAME.PRICE_MAP_RECOMMENDED_AGENCY_MAP, 0),
            ];
            sendAnalytics({
                av_geolocation_level: '%av_geolocation_level%',
                av_items: avItem,
                av_zone: AV_ZONE.PRICE_MAP,
                event: TRACKING_EVENT_TYPE.VIEW_ITEM_LIST,
            }, TRACKING_PARENT_ID);
            dispatchStatsEvent({
                payload: {
                    lastUserSearch: lastZipCode,
                    legacyBrandId: agency.id,
                    name: agency.agencyName,
                    place: EventPlace.PRICE_MAP,
                    zipCode: agency.address.zipCode,
                },
                type: InternalEventType.CONTAINED,
            });
        }
    }, [agency, lastZipCode]);
    if (isFetchAgencyByIdLoading)
        return _jsx(StandaloneSkeleton, {});
    if (!agency)
        return _jsx(StandaloneErrorCard, { t: t });
    const { agencyLogo, profileUrl, agencyName, address: { zipCode, city }, reviews, propertiesForSale, } = agency;
    const propertiesForSaleTranslations = {
        noProperties: t('wlac_no_propforsale'),
        properties: t(`wlac_nb_propforsale.${isPlural(propertiesForSale)}`, {
            count: propertiesForSale,
        }),
    };
    const { mediumContact, mediumLogo, mediumName } = mediums;
    const onClickTrackingEvent = (linkText) => {
        sendAnalytics({
            av_geolocation_level: '%av_geolocation_level%',
            av_items: [
                generateAVItemPricePages(agency, AV_LIST_NAME.PRICE_MAP_RECOMMENDED_AGENCY_MAP, 0),
            ],
            av_zone: AV_ZONE.PRICE_MAP,
            event: TRACKING_EVENT_TYPE.SELECT_ITEM,
            link_text: linkText,
        }, TRACKING_PARENT_ID);
        dispatchStatsEvent({
            payload: {
                lastUserSearch: lastZipCode,
                legacyBrandId: agency.id,
                name: agency.agencyName,
                place: EventPlace.PRICE_MAP,
                zipCode: agency.address.zipCode,
            },
            type: InternalEventType.REDIRECTED,
        });
    };
    return (_jsxs(Box, { backgroundColor: "color.background.default", borderColor: "color.background.subdued", borderRadius: "radius.16", borderStyle: "solid", borderWidth: "borderWidth.1", display: "flex", flexDirection: "column", paddingHorizontal: "spacing.16", paddingVertical: "spacing.8", rowGap: "spacing.16", width: {
            'breakpoint.base': MOBILE_WIDTH,
            'breakpoint.lg': DESKTOP_WIDTH,
        }, children: [_jsxs(Box, { display: "flex", flexDirection: "column", gap: "spacing.8", children: [_jsxs(Box, { alignItems: "center", display: "flex", gap: "spacing.8", children: [_jsx(Logo, { agencyLogo: agencyLogo, agencyName: agencyName, linkUrl: `${profileUrl}${mediumLogo}${GERMAN_PROFILE_ANCHOR}`, onClick: () => onClickTrackingEvent(AV_LINK_TEXT.LOGO) }), _jsx(Box, { as: "a", color: "color.background.constant.black", href: `${profileUrl}${mediumName}${GERMAN_PROFILE_ANCHOR}`, marginBottom: "spacing.none", target: "_blank", typography: "typography.body.16.boldUnderlined", width: AGENCY_NAME_SIZE, onClick: () => onClickTrackingEvent(AV_LINK_TEXT.NAME), children: agencyName })] }), _jsx(Address, { city: city, zipCode: zipCode }), _jsx(PropertiesForSale, { content: propertiesForSaleTranslations, propertiesCount: propertiesForSale }), reviews && (_jsx(Rating, { reviews: {
                            avgRating: reviews.avgRating,
                        } }))] }), _jsx(Button, { href: `${profileUrl}${mediumContact}${GERMAN_PROFILE_ANCHOR}`, isFullWidth: isMobile, size: "40", variant: "primary", isExternal: true, onPress: () => onClickTrackingEvent(AV_LINK_TEXT.CONTACT_CTA), children: t('wlac_primarycta') })] }));
};
export default Standalone;
